import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isBoolean } from "lodash";
import { defer, from, iif, map, Observable, of, switchMap } from "rxjs";
import { InstallationBackend } from "src/app/backend/api/installation.backend";
import { IntegrationBackend } from "src/app/backend/api/integration.backend";
import { Connection } from "src/app/backend/models/connection";
import { Installation } from "src/app/backend/models/installation";
import { Integration } from "src/app/backend/models/integration";
import { sntConfirm } from "src/app/common/dialog";
import { ConnectIntegrationFunctionService } from "src/app/modules/bootstrap/functions/integrations/connect-integration/connect-integration-function.service";
import {
  ConnectIntegrationConnectionResult,
  ConnectIntegrationConnectionResultType
} from "src/app/modules/bootstrap/functions/integrations/connect-integration/connect-integration.connector";
import { sntTranslate } from "src/app/support/translate";

export type InstallationsFunctionsConnectionResult = {
  connection: Connection | undefined;
  canceled: boolean;
  failed: boolean;
};

@Injectable()
export class InstallationsFunctionsService {
  private readonly integrationBackend = inject(IntegrationBackend);
  private readonly installationBackend = inject(InstallationBackend);
  private readonly connectIntegrationFunctionService = inject(
    ConnectIntegrationFunctionService
  );

  loadConnectionMeOrConnectToInstallation({
    installationId,
    isRefresh,
    refreshConnectionId
  }: {
    installationId: string;
    isRefresh?: boolean;
    refreshConnectionId?: string;
  }): Observable<InstallationsFunctionsConnectionResult> {
    return defer(() => {
      return this.installationBackend
        .loadConnectionMeByInstallation({
          installationId
        })
        .pipe(
          switchMap((connection: Connection | undefined) => {
            return iif(
              () => !refreshConnectionId && !!connection,
              defer(() =>
                of(<InstallationsFunctionsConnectionResult>{
                  connection
                })
              ),
              defer(() => {
                return this.loadInstallationAndIntegration({
                  installationId
                }).pipe(
                  switchMap(({ installation, integration }) => {
                    return from(
                      this.askUserCreateConnection({ integration, isRefresh })
                    ).pipe(
                      switchMap((userWantContinue: boolean) => {
                        return iif(
                          () => userWantContinue,
                          defer(() => {
                            return from(
                              this.connectIntegrationFunctionService.execute({
                                installation,
                                integration,
                                isRefresh,
                                refreshConnectionId,
                                phase: "execute"
                              })
                            ).pipe(
                              map(
                                (
                                  connectionResult: ConnectIntegrationConnectionResult
                                ) => {
                                  switch (connectionResult.result) {
                                    case ConnectIntegrationConnectionResultType.SUCCEED: {
                                      return <
                                        InstallationsFunctionsConnectionResult
                                      >{
                                        connection: connectionResult.connection
                                      };
                                    }
                                    case ConnectIntegrationConnectionResultType.FAILED: {
                                      return <
                                        InstallationsFunctionsConnectionResult
                                      >{
                                        failed: true
                                      };
                                    }
                                    case ConnectIntegrationConnectionResultType.CANCELED: {
                                      return <
                                        InstallationsFunctionsConnectionResult
                                      >{
                                        canceled: true
                                      };
                                    }
                                  }
                                }
                              )
                            );
                          }),
                          defer(() => {
                            return of(<InstallationsFunctionsConnectionResult>{
                              canceled: true
                            });
                          })
                        );
                      })
                    );
                  })
                );
              })
            );
          })
        );
    });
  }

  private async askUserCreateConnection({
    integration,
    isRefresh
  }: {
    integration: Integration;
    isRefresh?: boolean;
  }): Promise<boolean> {
    return await sntConfirm(
      integration.name,
      !isRefresh
        ? sntTranslate(
            marker(
              "E| necessario autenticare il proprio utente, vuoi proseguire?"
            )
          )
        : sntTranslate(
            marker(
              "L|autenticazione verso [name] è scaduta, è necessario aggiornarla. Vuoi proseguire?"
            ),
            {
              name: integration.name
            }
          )
            .replaceAll("[name]", integration.name)
            .replaceAll("|", "'")
    ).then((result) => (isBoolean(result) ? result : result.confirmed));
  }

  private loadInstallationAndIntegration({
    installationId
  }: {
    installationId: string;
  }): Observable<{ installation: Installation; integration: Integration }> {
    return this.installationBackend.loadById(installationId).pipe(
      switchMap((installation: Installation) => {
        return this.integrationBackend.load(installation.integrationId).pipe(
          map((integration: Integration) => {
            return {
              installation,
              integration
            };
          })
        );
      })
    );
  }
}
